import { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';

/**
 * Hook to detect if the current company is in test mode
 *
 * Test mode is determined by:
 * 1. The presence of specific test API keys (NMI tokenization/security keys)
 * 2. NOT having an active PayArc gateway configured
 * 3. NOT having a merchant application in "live" or "processing" status
 *
 * When in test mode, a red banner will be displayed at the top of the page
 * indicating that the account is in test mode and funds will not be moved.
 *
 * @returns {Object} Object containing isTestMode and isLoading states
 */
export const useTestMode = () => {
  const { user } = useAuth();
  const [isTestMode, setIsTestMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkTestMode = async () => {
      if (!user?.companyId) return;

      setIsLoading(true);
      try {
        const companyDoc = await getDoc(doc(db, 'companies', user.companyId));
        if (!companyDoc.exists()) return;

        const companyData = companyDoc.data();

        // Check if PayArc gateway is enabled and has real credentials
        const payarcConfig = companyData?.paymentGateways?.providers?.payarc;
        const hasPayarcGateway = payarcConfig?.enabled === true &&
                                  !!payarcConfig?.credentials?.apiToken;

        // Check if Stripe gateway is enabled and has real credentials (not test mode)
        const stripeConfig = companyData?.paymentGateways?.providers?.stripe;
        const hasStripeGateway = stripeConfig?.enabled === true &&
                                  stripeConfig?.mode === 'live' &&
                                  !!stripeConfig?.credentials?.secretKey;

        // If PayArc or Stripe gateway is enabled with real credentials, not in test mode
        if (hasPayarcGateway || hasStripeGateway) {
          setIsTestMode(false);
          return;
        }

        // Check if merchant application is in "live" or "processing" status
        const merchantAppQuery = query(
          collection(db, 'merchantApplications'),
          where('companyId', '==', user.companyId)
        );
        const merchantAppSnap = await getDocs(merchantAppQuery);

        if (!merchantAppSnap.empty) {
          const merchantData = merchantAppSnap.docs[0].data();
          const pipelineStatus = (merchantData.pipelineStatus || '').toLowerCase();

          // If merchant is live or processing, they're not in test mode
          if (pipelineStatus === 'live' || pipelineStatus === 'processing') {
            setIsTestMode(false);
            return;
          }
        }

        // Test API keys that indicate test mode (NMI sandbox keys)
        const testTokenizationKey = 'T3qmAr-ed6Yz9-DC3tCN-Y7nGfy';
        const testSecurityKey = 'JvMr9YHy6QDA2v2pjSJT9dA2r3u638xz';

        const hasTestTokenizationKey = companyData?.wikiPayitSettings?.tokenizationKey === testTokenizationKey;
        const hasTestSecurityKey = companyData?.wikiPayitSettings?.securityKey === testSecurityKey;

        // If any of the test keys are present, the account is in test mode
        setIsTestMode(hasTestTokenizationKey || hasTestSecurityKey);
      } catch (error) {
        console.error('Error checking test mode:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkTestMode();
  }, [user?.companyId]);

  return { isTestMode, isLoading };
}; 